<script setup lang="ts">
import { Minus, Plus, RotateCcw } from 'lucide-vue-next';
import { computed } from 'vue';
import ToolSliderField from '@/components/Tools/ToolSliderField.vue';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/composables/useTranslations';

const props = withDefaults(
    defineProps<{
        hasFile: boolean;
        loading: boolean;
        error?: string;
        currentPage: number;
        totalPages: number;
        emptyMessage?: string;
        renderingMessage?: string;
        zoomMin?: number;
        zoomMax?: number;
        showZoom?: boolean;
    }>(),
    {
        error: '',
        zoomMin: 50,
        zoomMax: 200,
        showZoom: true,
    },
);

const zoom = defineModel<number>('zoom', { default: 100 });

const emit = defineEmits<{
    'change-page': [page: number];
}>();

const { t } = useTranslations();

const safeTotalPages = computed(() => Math.max(1, props.totalPages || 1));

const emptyDisplay = computed(() => {
    if (props.emptyMessage !== undefined && props.emptyMessage !== '') {
        return props.emptyMessage;
    }

    return t(
        'tools.pdf.ui.pdf_live_preview_empty',
        'Upload a PDF to see a live preview',
    );
});

const renderingDisplay = computed(() => {
    if (props.renderingMessage !== undefined && props.renderingMessage !== '') {
        return props.renderingMessage;
    }

    return t('tools.pdf.ui.pdf_live_preview_rendering', 'Rendering page…');
});

const pageOfLine = computed(() =>
    t('tools.pdf.ui.pdf_live_preview_page_of', 'page {current} of {total}')
        .replace('{current}', String(props.currentPage))
        .replace('{total}', String(props.totalPages)),
);

const pageSliderLabel = computed(() =>
    t('tools.pdf.ui.crop_page_label', 'Page'),
);

const zoomLabel = computed(() => t('tools.pdf.ui.pdf_preview_zoom', 'Zoom'));

const safeZoom = computed({
    get: () =>
        Math.min(
            props.zoomMax,
            Math.max(props.zoomMin, Math.round(zoom.value)),
        ),
    set: (value: number) => {
        zoom.value = Math.min(
            props.zoomMax,
            Math.max(props.zoomMin, Math.round(value)),
        );
    },
});

function formatZoomPct(value: number): string {
    return `${value}%`;
}

function zoomBy(delta: number): void {
    safeZoom.value = safeZoom.value + delta;
}

function resetZoom(): void {
    safeZoom.value = 100;
}

function goTo(page: number): void {
    const clamped = Math.min(safeTotalPages.value, Math.max(1, page));
    emit('change-page', clamped);
}

function prev(): void {
    goTo(props.currentPage - 1);
}

function next(): void {
    goTo(props.currentPage + 1);
}
</script>

<template>
    <div class="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
        <p
            class="mb-2 text-sm font-medium text-gray-700 sm:mb-3 dark:text-gray-300"
        >
            <span class="block sm:inline">{{
                t('tools.pdf.ui.pdf_live_preview_title', 'Live Preview')
            }}</span>
            <template v-if="totalPages > 0">
                <span class="hidden font-normal text-gray-400 sm:inline">
                    —
                </span>
                <span
                    class="mt-0.5 block font-normal text-gray-400 sm:mt-0 sm:inline"
                >
                    {{ pageOfLine }}
                </span>
            </template>
        </p>

        <div
            v-if="!hasFile"
            class="flex min-h-48 items-center justify-center rounded-lg border-2 border-dashed border-border px-2 text-center text-xs text-muted-foreground sm:h-64 sm:text-sm"
        >
            {{ emptyDisplay }}
        </div>

        <div
            v-else
            class="relative max-h-[min(70vh,720px)] min-h-[10rem] overflow-auto rounded-lg bg-muted/60 shadow-md sm:min-h-[12rem] dark:bg-muted/40"
        >
            <div
                v-if="error"
                class="flex min-h-[12rem] items-center justify-center border border-amber-200 bg-amber-50 px-4 text-center text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
            >
                {{ error }}
            </div>
            <template v-else>
                <slot />
                <div
                    v-if="loading"
                    class="absolute inset-0 z-20 flex animate-pulse items-center justify-center rounded-lg bg-muted/90 px-3 text-center text-xs text-muted-foreground backdrop-blur-sm sm:text-sm"
                >
                    {{ renderingDisplay }}
                </div>
            </template>
        </div>

        <div v-if="hasFile && totalPages > 0" class="mt-3 space-y-2">
            <div
                v-if="showZoom"
                class="flex flex-wrap items-center gap-2 border-b border-border/60 pb-3"
            >
                <span
                    class="text-xs font-medium text-gray-600 dark:text-gray-400"
                    >{{ zoomLabel }}</span
                >
                <div class="flex items-center gap-1">
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        class="size-8 shrink-0"
                        :disabled="loading || safeZoom <= zoomMin"
                        :aria-label="
                            t('tools.pdf.ui.pdf_preview_zoom_out', 'Zoom out')
                        "
                        @click="zoomBy(-10)"
                    >
                        <Minus class="size-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        class="size-8 shrink-0"
                        :disabled="loading || safeZoom >= zoomMax"
                        :aria-label="
                            t('tools.pdf.ui.pdf_preview_zoom_in', 'Zoom in')
                        "
                        @click="zoomBy(10)"
                    >
                        <Plus class="size-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        class="h-8 px-2 text-xs"
                        :disabled="loading || safeZoom === 100"
                        @click="resetZoom"
                    >
                        <RotateCcw class="mr-1 size-3.5" />
                        {{ t('tools.pdf.ui.pdf_preview_zoom_reset', '100%') }}
                    </Button>
                </div>
                <ToolSliderField
                    v-model="safeZoom"
                    :label="zoomLabel"
                    :min="zoomMin"
                    :max="zoomMax"
                    :step="10"
                    :format-value="formatZoomPct"
                    :show-label="false"
                    wrapper-class="min-w-0 flex-1"
                    slider-class="min-h-8 touch-manipulation py-0"
                    :disabled="loading"
                />
            </div>
            <div
                class="flex touch-manipulation flex-wrap items-stretch gap-2 sm:items-center"
            >
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    class="min-h-10 flex-1 sm:min-h-8 sm:flex-none"
                    :disabled="loading || currentPage <= 1"
                    @click="prev"
                >
                    {{
                        t('tools.pdf.ui.pdf_live_preview_previous', 'Previous')
                    }}
                </Button>
                <div
                    class="flex min-w-0 flex-1 basis-[8.5rem] items-center gap-1.5 sm:flex-none sm:basis-auto"
                >
                    <input
                        :value="currentPage"
                        type="number"
                        min="1"
                        :max="safeTotalPages"
                        inputmode="numeric"
                        :aria-label="pageSliderLabel"
                        class="min-h-10 w-full max-w-[5.5rem] min-w-0 rounded-md border border-gray-200 px-2 py-2 text-center text-sm tabular-nums sm:min-h-8 sm:w-20 sm:py-1 dark:border-border dark:bg-muted dark:text-foreground"
                        @change="
                            goTo(
                                Number(
                                    ($event.target as HTMLInputElement).value,
                                ),
                            )
                        "
                    />
                    <span
                        class="shrink-0 text-xs text-gray-500 dark:text-gray-400"
                        >/ {{ safeTotalPages }}</span
                    >
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    class="min-h-10 flex-1 sm:min-h-8 sm:flex-none"
                    :disabled="loading || currentPage >= safeTotalPages"
                    @click="next"
                >
                    {{ t('tools.pdf.ui.pdf_live_preview_next', 'Next') }}
                </Button>
            </div>
            <ToolSliderField
                :label="pageSliderLabel"
                :model-value="currentPage"
                :min="1"
                :max="safeTotalPages"
                :step="1"
                :show-label="false"
                :show-value="false"
                wrapper-class="w-full"
                slider-class="min-h-10 touch-manipulation py-1 sm:min-h-8 sm:py-0"
                @update:model-value="goTo"
            />
        </div>

        <slot name="footer" />
    </div>
</template>
